import React, { useContext, useState, useEffect, useRef } from "react";
import styled, { css } from "styled-components";
import chroma from "chroma-js";
import debounce from "lodash/debounce";
import { Trans, useTranslation } from "react-i18next";
import { OverlayTrigger } from "components/common";
import { Alert, Button, ButtonGroup, BootstrapModalConfirm } from "components/bootstrap";
import { ScratchpadContext } from "contexts/ScratchpadProvider";
import InteractableModal from "components/common/InteractableModal";
import Icon from "components/common/Icon";
import Store from "logic/local-storage/Store";
import useHotkey from "hooks/useHotkey";
import copyToClipboard from "util/copyToClipboard";
const DEFAULT_SCRATCHDATA = "";
const TEXTAREA_ID = "scratchpad-text-content";
const STATUS_CLEARED = "scratchpad.status_cleared";
const STATUS_COPIED = "clipboard.copied";
const STATUS_AUTOSAVED = "scratchpad.status_auto_saved";
const STATUS_DEFAULT = "";
const ContentArea = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;
const Textarea = styled.textarea(
  ({ $copied, theme }) => css`
    width: 100%;
    padding: 3px;
    resize: none;
    flex: 1;
    margin: 15px 0 7px;
    border: 1px solid ${$copied ? theme.colors.variant.success : theme.colors.variant.lighter.default};
    box-shadow: inset 1px 1px 1px rgb(0 0 0 / 7.5%)
      ${$copied && `, 0 0 8px ${chroma(theme.colors.variant.success).alpha(0.4).css()}`};
    transition:
      border 150ms ease-in-out,
      box-shadow 150ms ease-in-out;
    font-family: ${theme.fonts.family.monospace};
    font-size: ${theme.fonts.size.body};

    &:focus {
      border-color: ${theme.colors.variant.light.info};
      outline: none;
    }
  `
);
const StyledAlert = styled(Alert)`
  && {
    padding: 6px 12px;
    margin: 6px 0 0;
    display: flex;
    align-items: center;
  }
`;
const AlertNote = styled.em`
  margin-left: 6px;
  flex: 1;
`;
const Footer = styled.footer(
  ({ theme }) => css`
    display: flex;
    align-items: center;
    padding: 7px 0 9px;
    border-top: 1px solid ${theme.colors.gray[80]};
  `
);
const StatusMessage = styled.span(
  ({ theme, $visible }) => css`
    flex: 1;
    color: ${theme.colors.variant.success};
    font-style: italic;
    opacity: ${$visible ? "1" : "0"};
    transition: opacity 150ms ease-in-out;
  `
);
const Scratchpad = () => {
  const { t } = useTranslation("common");
  const textareaRef = useRef();
  const statusTimeout = useRef();
  const { setScratchpadVisibility, localStorageItem } = useContext(ScratchpadContext);
  const scratchpadStore = Store.get(localStorageItem) || {};
  const [isSecurityWarningConfirmed, setSecurityWarningConfirmed] = useState(
    scratchpadStore.securityConfirmed || false
  );
  const [scratchData, setScratchData] = useState(scratchpadStore.value || DEFAULT_SCRATCHDATA);
  const [size, setSize] = useState(scratchpadStore.size || void 0);
  const [position, setPosition] = useState(scratchpadStore.position || void 0);
  const [statusMessage, setStatusMessage] = useState(STATUS_DEFAULT);
  const [showStatusMessage, setShowStatusMessage] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const writeData = (newData) => {
    const currentStorage = Store.get(localStorageItem);
    Store.set(localStorageItem, { ...currentStorage, ...newData });
  };
  const resetStatusTimer = () => {
    if (statusTimeout.current) {
      clearTimeout(statusTimeout.current);
    }
    statusTimeout.current = setTimeout(() => {
      setShowStatusMessage(false);
    }, 1e3);
  };
  const updateStatusMessage = (message) => {
    setStatusMessage(message);
    setShowStatusMessage(true);
    resetStatusTimer();
  };
  const handleChange = debounce(() => {
    const { value } = textareaRef.current;
    setScratchData(value);
    updateStatusMessage(STATUS_AUTOSAVED);
    writeData({ value });
  }, 500);
  const handleDrag = (newPosition) => {
    setPosition(newPosition);
    writeData({ position: newPosition });
  };
  const handleSize = (newSize) => {
    setSize(newSize);
    writeData({ size: newSize });
  };
  const handleGotIt = () => {
    setSecurityWarningConfirmed(true);
    writeData({ securityConfirmed: true });
  };
  const openConfirmClear = () => {
    setShowModal(true);
  };
  const handleCancelClear = () => {
    setShowModal(false);
  };
  const handleClearText = () => {
    setScratchData(DEFAULT_SCRATCHDATA);
    writeData({ value: DEFAULT_SCRATCHDATA });
    handleCancelClear();
    updateStatusMessage(STATUS_CLEARED);
  };
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.focus();
      textareaRef.current.value = scratchData;
    }
  }, [scratchData]);
  useHotkey({
    actionKey: "clear",
    scope: "scratchpad",
    callback: openConfirmClear,
    options: { enableOnFormTags: true, preventDefault: true }
  });
  const copyCallback = () => {
    copyToClipboard(scratchData).then(() => updateStatusMessage(STATUS_COPIED));
  };
  useHotkey({
    actionKey: "copy",
    scope: "scratchpad",
    callback: copyCallback,
    options: { enableOnFormTags: true }
  });
  return /* @__PURE__ */ React.createElement(
    InteractableModal,
    {
      title: t("scratchpad.title"),
      onClose: () => setScratchpadVisibility(false),
      onDrag: handleDrag,
      onResize: handleSize,
      size,
      position
    },
    /* @__PURE__ */ React.createElement(ContentArea, null, !isSecurityWarningConfirmed && /* @__PURE__ */ React.createElement(StyledAlert, { bsStyle: "warning" }, /* @__PURE__ */ React.createElement(AlertNote, null, /* @__PURE__ */ React.createElement(Trans, { t, i18nKey: "scratchpad.sensitive_warning", components: { bold: /* @__PURE__ */ React.createElement("strong", null) } })), /* @__PURE__ */ React.createElement(Button, { bsStyle: "link", bsSize: "sm", onClick: handleGotIt }, t("scratchpad.got_it"))), /* @__PURE__ */ React.createElement(
      Textarea,
      {
        ref: textareaRef,
        onChange: handleChange,
        id: TEXTAREA_ID,
        $copied: statusMessage === STATUS_COPIED,
        spellCheck: false
      }
    ), /* @__PURE__ */ React.createElement(Footer, null, /* @__PURE__ */ React.createElement(OverlayTrigger, { placement: "right", trigger: ["hover", "focus"], overlay: /* @__PURE__ */ React.createElement(React.Fragment, null, t("scratchpad.help_overlay")) }, /* @__PURE__ */ React.createElement(Button, { bsStyle: "link" }, /* @__PURE__ */ React.createElement(Icon, { name: "help" }))), /* @__PURE__ */ React.createElement(StatusMessage, { $visible: showStatusMessage }, /* @__PURE__ */ React.createElement(Icon, { name: statusMessage === STATUS_COPIED ? "content_copy" : "save", type: "regular" }), " ", statusMessage && t(statusMessage)), /* @__PURE__ */ React.createElement(ButtonGroup, null, /* @__PURE__ */ React.createElement(Button, { id: "scratchpad-actions", onClick: copyCallback, title: t("scratchpad.copy") }, /* @__PURE__ */ React.createElement(Icon, { name: "content_copy" })), /* @__PURE__ */ React.createElement(Button, { onClick: openConfirmClear, title: t("scratchpad.clear") }, /* @__PURE__ */ React.createElement(Icon, { name: "delete" }))))),
    /* @__PURE__ */ React.createElement(
      BootstrapModalConfirm,
      {
        showModal,
        title: t("confirm_leave.question"),
        onConfirm: handleClearText,
        onCancel: handleCancelClear
      },
      t("scratchpad.clear_confirm")
    )
  );
};
export default Scratchpad;
